import { AUTH, db } from '@util/firebase';
import {
  PurchaseEntryDocument,
  purchaseEntryDocumentSchema,
} from '@util/types/firestore/purchase_entry';
import { Pagination } from '@util/types/paginator';
import {
  collection,
  CollectionReference,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  where,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';

//
// Utilities
//

const DEFAULT_LIMIT = 12;

const defaultPagination: Pagination = {
  page: 1,
  totalPages: 1,
  itemsPerPage: DEFAULT_LIMIT,
  totalResults: 0,
};

export const purchaseEntryRef = collection(
  db,
  'purchase_entries'
) as CollectionReference<PurchaseEntryDocument>;

//
// Data fetching functions
//

/** Allocates the next global purchase-entry ID on the authenticated server. */
export async function getNextPurchaseEntryID() {
  const user = AUTH.currentUser;
  if (!user) {
    throw new Error('You must be signed in to create a purchase entry.');
  }

  const idToken = await user.getIdToken();
  const response = await fetch('/api/purchase-entries/next-id', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
  const result = (await response.json()) as { error?: string; id?: string };

  if (!response.ok || !result.id) {
    throw new Error(result.error ?? 'Unable to allocate a purchase entry ID.');
  }

  return result.id;
}

// Firestore rules enforce that the signed-in reseller owns this entry.
export async function createPurchaseEntry(data: PurchaseEntryDocument) {
  const parsedData = purchaseEntryDocumentSchema.parse(data);
  const docRef = doc(purchaseEntryRef, parsedData.id);
  await setDoc(docRef, parsedData);
}

// could just refactor getPurchaseEntries and getPurchaseEntriesPaginated into one...
export async function getPurchaseEntries(args: {
  limit?: number;
  ownerUID: string;
}) {
  const q = query(
    purchaseEntryRef,
    where('owner_uid', '==', args.ownerUID),
    orderBy('date_of_purchase', 'desc')
  );
  const snapshot = await getDocs(q);

  const data: PurchaseEntryDocument[] = snapshot.docs.map((doc) => {
    return doc.data();
  });

  if (typeof data === 'undefined') {
    return { results: [] };
  }
  return { results: data };
}

// FIXME: doesn't start at correct spot
export async function getPurchaseEntriesPaginated(args: {
  page: number;
  limit?: number;
  ownerUID: string | null;
  isSupport?: boolean;
}) {
  const resultLimit = args.limit ?? DEFAULT_LIMIT;

  if (!args.ownerUID && !args.isSupport) {
    return;
  }

  // Get total counts
  const noLimitQ = query(
    purchaseEntryRef,
    ...(!!args.ownerUID ? [where('owner_uid', '==', args.ownerUID)] : []),
    orderBy('date_of_purchase', 'desc')
  );
  const count = (await getCountFromServer(noLimitQ)).data().count;
  const totalPages = Math.ceil(count / resultLimit);

  // handle pagination
  let lastVisible = null;
  if (args.page > 1 && args.page <= totalPages) {
    const q = query(
      purchaseEntryRef,
      ...(!!args.ownerUID ? [where('owner_uid', '==', args.ownerUID)] : []),
      orderBy('date_of_purchase', 'desc'),
      limit((args.page - 1) * resultLimit)
    );

    const snap = await getDocs(q);
    lastVisible = snap.docs[snap.docs.length - 1];
  }

  const q = query(
    purchaseEntryRef,
    ...(!!args.ownerUID ? [where('owner_uid', '==', args.ownerUID)] : []),
    orderBy('date_of_purchase', 'desc'),
    limit(resultLimit),
    ...(lastVisible ? [startAfter(lastVisible)] : [])
  );
  const snapshot = await getDocs(q);

  const data = snapshot.docs.map((doc) => {
    return doc.data();
  });

  if (typeof data === 'undefined') {
    return {
      results: [],
      pageInfo: defaultPagination,
    };
  }
  return {
    results: data,
    pageInfo: {
      total: count,
      page: args.page,
      totalPages,
    },
  };
}

export async function getSellersFromPurchaseEntries(args: {
  limit?: number;
  ownerUID: string;
}) {
  const purchaseEntries = (await getPurchaseEntries(args)).results;

  const sellers = purchaseEntries?.map((entry) => entry.seller_bought_from);

  const seenNames = new Set();

  const uniqueSellers = sellers.filter((seller) => {
    return !seenNames.has(seller.name) ? seenNames.add(seller.name) : false;
  });

  return uniqueSellers;
}

export async function uploadSellerPicture(sellerName: string, data: Blob) {
  if (!sellerName) {
    throw 'Seller name must not be empty for photo upload!';
  }
  const storage = getStorage();
  const path = `purchase_entries/seller-${sellerName}`;
  const userImagesRef = ref(storage, path);
  const res = await uploadBytes(userImagesRef, data);
  return res.metadata.fullPath;
}

export async function getPurchaseEntryById(id: string) {
  const docRef = doc(purchaseEntryRef, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data();
  } else {
    return null;
  }
}
